/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ArrowUpRight, RotateCcw, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { Loading } from '../../components/Loading';
import { Foto } from '../Sustainability/comum';
import {
  ROTULOS,
  anoDe,
  aplicarMetadados,
  carregarNoticias,
  contagem,
  dataPorExtenso,
  emFocoDe,
  partesDaData,
  semAcento,
} from './dados';
import * as S from './style';

// Quantas notícias a lista mostra de cada vez.
const POR_VEZ = 6;

export const enderecoDa = (noticia) => `/noticias/${noticia.endereco}`;

// O título com o trecho em destaque (verde). O trecho tem que estar escrito
// igual no título; se não estiver, o título sai todo normal.
export const TituloComDestaque = ({ titulo = '', destaque }) => {
  const i = destaque ? titulo.lastIndexOf(destaque) : -1;
  if (i < 0) return <h1>{titulo}</h1>;
  return (
    <h1>
      {titulo.slice(0, i)}
      <br />
      <em>{destaque}</em>
      {titulo.slice(i + destaque.length)}
    </h1>
  );
};

const EmFoco = ({ noticia, legenda }) => (
  <S.EmFoco aria-label={ROTULOS.emFoco}>
    <S.FocoTexto>
      <S.Kicker>{ROTULOS.emFoco}</S.Kicker>
      <time dateTime={noticia.data.slice(0, 10)}>{dataPorExtenso(noticia.data)}</time>
      <h2>{noticia.titulo}</h2>
      {noticia.resumo && <p>{noticia.resumo}</p>}
      <S.FocoAcao>
        <Link to={enderecoDa(noticia)} aria-label={`${ROTULOS.lerNoticia}: ${noticia.titulo}`}>
          <span>{ROTULOS.lerNoticia}</span>
          <span aria-hidden="true">
            <ArrowUpRight size={18} />
          </span>
        </Link>
      </S.FocoAcao>
    </S.FocoTexto>
    {noticia.capa && (
      <S.FocoFoto to={enderecoDa(noticia)} tabIndex={-1} aria-hidden="true">
        <Foto id={noticia.capa} largura={1200} alt="" loading="eager" />
        {legenda && <span>{legenda}</span>}
      </S.FocoFoto>
    )}
  </S.EmFoco>
);

const Linha = ({ noticia }) => {
  const { dia, mes, ano } = partesDaData(noticia.data);
  return (
    <S.Linha $semFoto={!noticia.capa}>
      <S.LinhaData>
        <span>{dia}</span>
        <small>
          {mes} · {ano}
        </small>
      </S.LinhaData>
      <S.LinhaTexto>
        <h3>{noticia.titulo}</h3>
        {noticia.resumo && <p>{noticia.resumo}</p>}
        <S.Acao to={enderecoDa(noticia)} aria-label={`${ROTULOS.lerNoticia}: ${noticia.titulo}`}>
          {ROTULOS.lerNoticia} <ArrowRight size={17} aria-hidden="true" />
        </S.Acao>
      </S.LinhaTexto>
      {noticia.capa && (
        <S.LinhaFoto to={enderecoDa(noticia)} tabIndex={-1} aria-hidden="true">
          <Foto id={noticia.capa} largura={480} alt="" />
        </S.LinhaFoto>
      )}
    </S.Linha>
  );
};

export const FaixaFinal = ({ faixa }) => {
  if (!faixa?.frase) return null;
  return (
    <S.Container>
      <S.FaixaFinal>
        {faixa.complemento && <span>{faixa.complemento}</span>}
        <p>
          {faixa.frase.split('\n').map((linha, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {linha}
            </React.Fragment>
          ))}
        </p>
      </S.FaixaFinal>
    </S.Container>
  );
};

export const News = () => {
  const [noticias, setNoticias] = useState(null);
  const [pronto, setPronto] = useState(false);
  const [busca, setBusca] = useState('');
  const [ano, setAno] = useState(null);
  const [quantas, setQuantas] = useState(POR_VEZ);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    setLoading(true);
    if (!token) return;
    carregarNoticias(token)
      .then(setNoticias)
      .catch((e) => console.log(e.message))
      .finally(() => {
        setPronto(true);
        setLoading(false);
      });
  }, [token]);

  // A página tem o próprio topo: o banner do Header fica desligado.
  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  useEffect(() => {
    if (!noticias) return undefined;
    return aplicarMetadados(noticias.metadados);
  }, [noticias]);

  const foco = useMemo(() => (noticias ? emFocoDe(noticias) : null), [noticias]);

  // Do mais recente ao mais antigo (a lista já vem por data).
  const anos = useMemo(
    () => [...new Set((noticias?.lista || []).map((n) => anoDe(n.data)))].filter(Boolean),
    [noticias],
  );

  // A notícia em foco não se repete na lista.
  const filtradas = useMemo(() => {
    const termo = semAcento(busca.trim());
    return (noticias?.lista || []).filter(
      (n) =>
        n !== foco &&
        (!ano || anoDe(n.data) === ano) &&
        (!termo || semAcento(`${n.titulo} ${n.resumo}`).includes(termo)),
    );
  }, [noticias, foco, busca, ano]);

  // Filtro novo começa do primeiro bloco de novo.
  useEffect(() => setQuantas(POR_VEZ), [busca, ano]);

  if (!pronto) return <Loading />;
  if (!noticias) return null;

  const { topo } = noticias;
  const visiveis = filtradas.slice(0, quantas);
  const filtrando = Boolean(busca.trim() || ano);
  const recentes = anos.slice(0, 2);
  const anteriores = anos.slice(2);
  const limpar = () => {
    setBusca('');
    setAno(null);
  };

  return (
    <S.Pagina>
      <S.Container>
        <S.TrilhaDaPagina />

        <S.Abertura>
          <TituloComDestaque titulo={topo.titulo} destaque={topo.destaque} />
          {(topo.chamada || topo.descricao) && (
            <S.Nota>
              {topo.chamada && <strong>{topo.chamada}</strong>}
              {topo.descricao && <p>{topo.descricao}</p>}
            </S.Nota>
          )}
        </S.Abertura>

        {foco && <EmFoco noticia={foco} legenda={noticias.emFoco.legendaFoto} />}

        <S.Arquivo>
          <S.Filtros aria-label={ROTULOS.filtrosTitulo}>
            <p>{ROTULOS.filtrosChapeu}</p>
            <h2>{ROTULOS.filtrosTitulo}</h2>

            <label htmlFor="busca-noticias">{ROTULOS.palavraChave}</label>
            <S.CampoBusca>
              <Search size={16} aria-hidden="true" />
              <input
                id="busca-noticias"
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={ROTULOS.busca}
              />
              {busca && (
                <button type="button" aria-label="Limpar busca" onClick={() => setBusca('')}>
                  <X size={16} />
                </button>
              )}
            </S.CampoBusca>

            {anos.length > 0 && (
              <fieldset>
                <legend>{ROTULOS.ano}</legend>
                <S.Anos>
                  <button type="button" aria-pressed={!ano} onClick={() => setAno(null)}>
                    {ROTULOS.todos}
                  </button>
                  {/* os dois anos mais recentes viram botão; os anteriores, uma lista */}
                  {recentes.map((a) => (
                    <button key={a} type="button" aria-pressed={ano === a} onClick={() => setAno(a)}>
                      {a}
                    </button>
                  ))}
                </S.Anos>
                {anteriores.length > 0 && (
                  <>
                    <S.RotuloOutrosAnos htmlFor="outros-anos">{ROTULOS.outrosAnos}</S.RotuloOutrosAnos>
                    <S.OutrosAnos
                      id="outros-anos"
                      $ativo={anteriores.includes(ano)}
                      value={anteriores.includes(ano) ? ano : ''}
                      onChange={(e) => setAno(e.target.value || null)}
                    >
                      <option value="">{ROTULOS.selecioneAno}</option>
                      {anteriores.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </S.OutrosAnos>
                  </>
                )}
              </fieldset>
            )}

            <S.Limpar type="button" onClick={limpar} disabled={!filtrando}>
              {ROTULOS.limpar} <RotateCcw size={12} aria-hidden="true" />
            </S.Limpar>
          </S.Filtros>

          <div>
            <S.Contagem>
              <h2>{ROTULOS.publicacoes}</h2>
              <span aria-live="polite">{contagem(filtradas.length, ROTULOS.noticia)}</span>
            </S.Contagem>

            {visiveis.length ? (
              visiveis.map((n) => <Linha key={n.id} noticia={n} />)
            ) : (
              <S.Vazio>{ROTULOS.vazio}</S.Vazio>
            )}
          </div>
        </S.Arquivo>

        {/* Fora do bloco dos filtros: a lateral termina na última notícia e o
            botão fica no meio da página. */}
        {filtradas.length > quantas && (
          <S.MaisPublicacoes type="button" onClick={() => setQuantas(quantas + POR_VEZ)}>
            {ROTULOS.maisPublicacoes}
          </S.MaisPublicacoes>
        )}

        {visiveis.length > 0 && filtradas.length <= quantas && <S.Fim>{ROTULOS.fim}</S.Fim>}
      </S.Container>

      <FaixaFinal faixa={noticias.faixaFinal} />
    </S.Pagina>
  );
};
